import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  inject,
  NgZone,
  OnDestroy,
  signal,
  Signal,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { animate, style, transition, trigger } from '@angular/animations';
import { DomSanitizer } from '@angular/platform-browser';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { ActivatedRoute } from '@angular/router';
import {
  CoinwiseEntriesService,
  CoinwiseUser,
  GiveawayTestOptions,
} from '../services/coinwise-entries.service';

/** Vertical scroll speed of the entrant list, px per 60fps-frame (~60px/s). */
const SCROLL_PX_PER_FRAME = 1;

@Component({
  selector: 'app-coinwise-entries',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './coinwise-entries.component.html',
  styleUrls: ['./coinwise-entries.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // `?scale=` renders the whole overlay larger via CSS zoom (crisp — text is
  // re-rasterised, not upscaled). Default 1. Lets the OBS source be cropped/
  // positioned as before, just bigger.
  host: { '[style.zoom]': 'scale' },
  animations: [
    trigger('appearAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(30px)' }),
        animate(
          '500ms ease-out',
          style({ opacity: 1, transform: 'translateY(0)' })
        ),
      ]),
    ]),
  ],
})
export class CoinwiseEntriesComponent implements AfterViewInit, OnDestroy {
  @ViewChild('viewport') private viewport!: ElementRef<HTMLDivElement>;
  @ViewChild('track') private track!: ElementRef<HTMLDivElement>;
  @ViewChild('primarySeq') private primarySeq?: ElementRef<HTMLDivElement>;
  @ViewChild('dupSeq') private dupSeq?: ElementRef<HTMLDivElement>;
  @ViewChild('entriesFrameVideo')
  set entriesFrameVideoRef(videoRef: ElementRef<HTMLVideoElement> | undefined) {
    if (videoRef?.nativeElement) {
      this.ensureFrameVideoPlayback(videoRef.nativeElement);
    }
  }

  private iconRegistry = inject(MatIconRegistry);
  private sanitizer = inject(DomSanitizer);
  private readonly entriesService = inject(CoinwiseEntriesService);
  private readonly zone = inject(NgZone);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly route = inject(ActivatedRoute);
  private readonly test = this.readTestConfig();

  /** `?scale=` zoom factor for the whole overlay (default 1). */
  readonly scale = this.readScale();

  /** True only when the list overflows the viewport — gates the seamless-loop
   *  duplicate so a short list (e.g. 3 winners) is not shown twice. */
  readonly loop = signal(false);

  public readonly users: Signal<CoinwiseUser[]> = this.entriesService.getUsers();
  public readonly giveawayData = this.entriesService.getGiveawayData();

  // Seamless vertical credits-loop: the list is rendered TWICE and the track is
  // translated up; once it passes one list-height we wrap by subtracting that
  // height, which is invisible because the second copy is identical. No native
  // scrollTop, no exponential duplication — bounded DOM, smooth GPU transform.
  private rafId: number | null = null;
  private offsetY = 0;
  private oneListHeight = 0;
  private viewportHeight = 0;
  private boundsDirty = true;
  private lastFrameTime = 0;
  private readonly onResize = () => { this.boundsDirty = true; };

  // Ticking clock so the "Giveaway starts in M:SS" header counts down live
  // (OnPush would otherwise freeze it between data changes).
  private readonly clock = signal(Date.now());
  private clockTimer: number | null = null;

  constructor() {
    this.registerCustomIcons();
    if (this.test) {
      this.entriesService.startTestMode(this.test);
    } else {
      this.entriesService.startUsersPolling();
    }
    // Re-measure the loop bounds whenever the entrant list changes.
    effect(() => {
      this.users();
      this.boundsDirty = true;
    });

    // Run a 1s clock only while counting down, so the header ticks down.
    effect(() => {
      if (this.giveawayData()?.state === 'countdown') this.startClock();
      else this.stopClock();
    });
  }

  private startClock(): void {
    if (this.clockTimer !== null) return;
    this.clock.set(Date.now());
    this.clockTimer = window.setInterval(() => this.clock.set(Date.now()), 1000);
  }

  private stopClock(): void {
    if (this.clockTimer !== null) {
      clearInterval(this.clockTimer);
      this.clockTimer = null;
    }
  }

  /** `?scale=1.4` → render the overlay 1.4× larger (crisp). Clamped to a sane range. */
  private readScale(): number {
    const v = Number(this.route.snapshot.queryParamMap.get('scale'));
    return Number.isFinite(v) && v > 0 ? Math.min(4, Math.max(0.3, v)) : 1;
  }

  /** `?testMode=1&max=&rate=&keyword=&countdown=&winners=&hold=&loop=` →
   *  simulate a full giveaway flow client-side (no backend). */
  private readTestConfig(): GiveawayTestOptions | null {
    const q = this.route.snapshot.queryParamMap;
    const on = q.get('testMode');
    if (!on || on === '0' || on === 'false') return null;
    const num = (key: string, def: number) => {
      const v = Number(q.get(key));
      return Number.isFinite(v) && v > 0 ? v : def;
    };
    return {
      max: Math.round(num('max', 50)),
      rate: num('rate', 3),
      keyword: q.get('keyword') || 'WIN',
      countdown: q.get('countdown') !== null ? Math.max(0, Number(q.get('countdown')) || 0) : 5,
      winners: Math.round(num('winners', 3)),
      hold: num('hold', 10),
      loop: ['1', 'true', 'yes'].includes((q.get('loop') || '').toLowerCase()),
    };
  }

  ngAfterViewInit(): void {
    window.addEventListener('resize', this.onResize, { passive: true });
    this.zone.runOutsideAngular(() => {
      this.rafId = requestAnimationFrame((t) => this.tick(t));
    });
  }

  private registerCustomIcons() {
    this.iconRegistry.addSvgIcon('twitch', this.sanitizer.bypassSecurityTrustResourceUrl('/assets/icons/twitch.svg'));
    this.iconRegistry.addSvgIcon('youtube', this.sanitizer.bypassSecurityTrustResourceUrl('/assets/icons/youtube.svg'));
  }

  getUserIcon(user: CoinwiseUser): string {
    return user.is_youtube ? 'youtube' : 'twitch';
  }

  private ensureFrameVideoPlayback(video: HTMLVideoElement): void {
    video.muted = true;
    video.defaultMuted = true;
    video.autoplay = true;
    video.loop = true;
    video.playsInline = true;

    const tryPlay = () => {
      void video.play().catch(() => {
        // Keep silent: autoplay can be blocked until browser decides media is ready.
      });
    };

    tryPlay();
    video.addEventListener('canplay', tryPlay, { once: true });
  }

  getHeader(): string {
    const data = this.giveawayData();
    if (!data) return '';
    if (data.state === 'countdown') {
      const seconds = Math.max(0, Math.ceil((data.start_time! * 1000 - this.clock()) / 1000));
      return `Giveaway starts in ${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`;
    }
    return data.state === 'winners' ? 'WINNERS!' : `Type: ${data.keyword || ''} to enter`;
  }

  getPrizeText(): string {
    const data = this.giveawayData();
    if (!data) return '';

    const count = data.subscription_count;
    const title = data.subscription_title?.trim();

    if (title && count) return `${title} x${count}`;
    if (title) return `Coinwise ${title}`;
    if (count) return `Coinwise Prize x${count}`;
    return 'Coinwise Prize';
  }

  private tick(time: number): void {
    this.rafId = requestAnimationFrame((t) => this.tick(t));

    const viewport = this.viewport?.nativeElement;
    const track = this.track?.nativeElement;
    if (!viewport || !track) return; // overlay hidden (idle state) — nothing to do

    // Measure only when the list changed or on resize (no per-frame reflow).
    if (this.boundsDirty) this.remeasure(viewport);

    // Not overflowing → show the list once, statically (no duplicate, no scroll).
    // Always pin to the top: when switching from an overflowing (scrolling)
    // list to a short one (e.g. a single winner) the stale scroll transform
    // would otherwise push the items off-screen.
    if (!this.loop()) {
      this.offsetY = 0;
      track.style.transform = 'translate3d(0, 0, 0)';
      this.lastFrameTime = time;
      return;
    }

    const dt = this.lastFrameTime ? Math.min(2, (time - this.lastFrameTime) / 16.6667) : 1;
    this.lastFrameTime = time;

    this.offsetY += SCROLL_PX_PER_FRAME * dt;
    if (this.offsetY >= this.oneListHeight) this.offsetY -= this.oneListHeight;
    track.style.transform = `translate3d(0, ${-this.offsetY}px, 0)`;
  }

  /** Decide whether the list overflows (→ loop + render the duplicate), and
   *  measure the loop period. Toggling the duplicate re-renders synchronously
   *  (detectChanges) so the measurement below is correct in the same frame. */
  private remeasure(viewport: HTMLElement): void {
    const primary = this.primarySeq?.nativeElement;
    if (!primary) {
      this.boundsDirty = false;
      return;
    }

    const shouldLoop = primary.offsetHeight > viewport.clientHeight + 1;
    if (shouldLoop !== this.loop()) {
      this.loop.set(shouldLoop);
      this.cdr.detectChanges(); // add/remove the duplicate sequence now
      if (!shouldLoop) this.offsetY = 0;
    }

    const dup = this.dupSeq?.nativeElement;
    // Loop period = distance from the primary copy to its duplicate (robust to
    // margin spacing); falls back to the primary height when not looping.
    this.oneListHeight =
      shouldLoop && dup ? dup.offsetTop - primary.offsetTop : primary.offsetHeight;
    this.boundsDirty = false;
  }

  trackByUserId(index: number, user: CoinwiseUser) {
    return user.id ?? index;
  }

  ngOnDestroy(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    window.removeEventListener('resize', this.onResize);
    this.stopClock();
    if (this.test) {
      this.entriesService.stopTestMode();
    } else {
      this.entriesService.stopUsersPolling();
    }
  }
}
